import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { CheckCircleRounded, RadioButtonUncheckedRounded } from '@mui/icons-material';
import OnboardingClient, { OnboardingWorkspace } from '../../apiClients/OnboardingClient';

const OnboardingChecklistCard = () => {
    const history = useHistory();
    const [workspace, setWorkspace] = useState<OnboardingWorkspace | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        new OnboardingClient().GetWorkspace(controller.signal)
            .then(setWorkspace)
            // Organizations created before guided onboarding do not have a
            // workspace. That is expected and should not surface an error.
            .catch(() => undefined);
        return () => controller.abort();
    }, []);

    if (!workspace)
        return null;

    const items = [
        ['Company context', workspace.Checklist.CompanyProfile],
        ['First position', workspace.Checklist.Position],
        ['Success profile', workspace.Checklist.SuccessProfile],
        ['Employees', workspace.Checklist.Employees],
        ['Activation', workspace.Checklist.Activation],
    ] as Array<[string, boolean]>;
    const remaining = items.filter(([, complete]) => !complete).length;
    if (remaining === 0)
        return null;

    return (
        <Paper variant="outlined" aria-label="Organization setup checklist" sx={{ p: { xs: 1.5, sm: 2 }, width: '100%' }}>
            <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <div>
                        <Typography variant="h6" fontWeight={800}>Finish setting up Synergist Pro</Typography>
                        <Typography variant="body2" color="text.secondary">Your progress is saved. Complete the remaining items whenever you’re ready.</Typography>
                    </div>
                    <Chip label={`${remaining} remaining`} size="small" />
                </Stack>
                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                    {items.map(([label, complete]) => <Chip key={label} variant="outlined" color={complete ? 'success' : 'default'} icon={complete ? <CheckCircleRounded /> : <RadioButtonUncheckedRounded />} label={label} />)}
                </Stack>
                <Alert severity="info" action={<Button onClick={() => history.push('/Onboarding')} sx={{ minHeight: 44 }}>Resume setup</Button>}>
                    Setup collaborators are separate from billable employees.
                </Alert>
            </Stack>
        </Paper>
    );
};

export default OnboardingChecklistCard;
